import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const ProductsSection = () => {
  const products = [
    {
      id: "preconfigured-agents",
      name: "Hire Preconfigured AI Agents",
      tagline: "Deploy Business-Ready AI Agents Instantly",
      description:
        "Launch powerful AI agents across customer support, HR, finance, operations, marketing, and data analytics. Plug-and-play solutions with built-in human oversight workflows.",
      icon: "/img/product-trio-1.png",
      color: "blue",
      features: [
        "Customer Support AI",
        "HR & Employee Assistant",
        "Finance & Invoice AI",
        "Operations Workflow AI",
        "Marketing & Growth AI",
        "Founder's Chief of Staff",
        "Data & Reporting Analyst",
      ],
      pricing: "Standard",
      cta: "Deploy Your Agent",
      popular: true,
    },
    {
      id: "custom-agent",
      name: "Build Your Own AI Agent",
      tagline: "Custom AI Development for Your Business",
      description:
        "Co-create a custom agent built on your data, workflows, logic, and compliance requirements. Perfect for high-growth startups and SMEs with unique processes.",
      icon: "/img/product-trio-2.png",
      color: "purple",
      features: [
        "Workflow mapping & design",
        "Knowledge & data integration",
        "Skills & intent configuration",
        "Human-in-the-loop setup",
        "Deployment & optimization",
        "Ongoing support & training",
      ],
      pricing: "Premium",
      cta: "Build My Agent",
      popular: false,
    },
    {
      id: "event-ai-agent",
      name: "Event AI Agent",
      tagline: "Professional Event Management & Registrations",
      description:
        "Host professional events with AI-powered management. Create event pages, manage registrations, send invites, and track attendees with intelligent automation.",
      icon: "/img/product-trio-3.png",
      color: "green",
      features: [
        "Beautiful event pages",
        "Custom registration forms",
        "AI-powered email campaigns",
        "Attendee management",
        "Zoom/Google Meet integration",
        "Analytics & insights",
      ],
      pricing: "Freemium",
      cta: "Create Event",
      popular: true,
    },
    {
      id: "scheduling-ai-agent",
      name: "Scheduling AI Agent",
      tagline: "Intelligent Calendar & Booking Management",
      description:
        "AI-powered calendar booking pages for 1-on-1 meetings. Automatically coordinate calendars, send reminders, and optimize meeting times across time zones.",
      icon: "/img/product-trio-3.png",
      color: "blue",
      features: [
        "Unlimited booking pages",
        "Calendar integrations (Google, Apple)",
        "AI availability optimization",
        "Automated reminders",
        "Multi-timezone support",
        "Free Forever",
      ],
      pricing: "Coming Soon",
      cta: "Join Waitlist",
      popular: false,
    },
    {
      id: "meeting-ai-agent",
      name: "Meeting AI Agent",
      tagline: "AI Meeting Notes & Transcription",
      description:
        "Never take notes again. Our AI agent joins meetings, records, transcribes, and generates smart summaries with action items automatically extracted.",
      icon: "/img/product-trio-3.png",
      color: "purple",
      features: [
        "Auto-join meetings",
        "Real-time transcription",
        "AI-generated summaries",
        "Action item extraction",
        "Speaker identification",
        "Searchable meeting library",
      ],
      pricing: "Coming Soon",
      cta: "Join Waitlist",
      popular: false,
    },
    {
      id: "industry-solutions",
      name: "Industry-Specific AI Solutions",
      tagline: "AI Tailored to Your Sector",
      description:
        "Get AI solutions pre-trained for your industry. Manufacturing, retail, healthcare, professional services, education — intelligence built for your specific sector needs.",
      icon: "/img/product-trio-2.png",
      color: "green",
      features: [
        "Pre-trained industry workflows",
        "Domain compliance built-in",
        "Multi-agent orchestration",
        "Training data templates",
        "Launch & optimization support",
        "Sector-specific integrations",
      ],
      pricing: "Enterprise",
      cta: "Request Solution",
      popular: false,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: "from-blue-500 to-blue-600",
        text: "text-blue-700",
        border: "border-blue-200",
        badge: "bg-blue-100 text-blue-700",
      },
      purple: {
        bg: "from-purple-500 to-purple-600",
        text: "text-purple-700",
        border: "border-purple-200",
        badge: "bg-purple-100 text-purple-700",
      },
      green: {
        bg: "from-green-500 to-green-600",
        text: "text-green-700",
        border: "border-green-200",
        badge: "bg-green-100 text-green-700",
      },
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 mb-6 text-sm font-semibold text-primary-700 bg-primary-100 rounded-full">
            Core Offerings
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your AI Workforce{" "}
            <span className="text-gradient">Starts Here</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Intelligent agents trained for real SME and startup operations — ready to deploy, adapt, and scale.
            Launch preconfigured agents instantly, build custom solutions, or request industry-specific AI tailored to your sector.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {products.map((product, index) => {
            const colors = getColorClasses(product.color);
            return (
              <motion.div
                key={product.id}
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                className={`glass-effect rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border ${colors.border} relative`}
              >
                {product.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-semibold rounded-full shadow-lg">
                      ✓ Available Now
                    </span>
                  </div>
                )}
                {product.pricing === "Coming Soon" && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-semibold rounded-full shadow-lg">
                      Coming Soon
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden mx-auto mb-4">
                    <img
                      src={product.icon}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {product.name}
                  </h3>
                  <p className={`text-lg font-semibold ${colors.text} mb-4`}>
                    {product.tagline}
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    {product.description}
                  </p>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Key Features:
                  </h4>
                  <ul className="space-y-2">
                    {product.features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm text-gray-600"
                      >
                        <div
                          className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${colors.bg}`}
                        ></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between mb-6">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${colors.badge}`}
                  >
                    {product.pricing}
                  </span>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      {product.pricing === "Free" ? "$0" :
                       product.pricing === "Freemium" ? "Free" :
                       product.pricing === "Coming Soon" ? "TBA" :
                       product.pricing === "Standard" ? "From $99" :
                       product.pricing === "Premium" ? "From $499" :
                       product.pricing === "Enterprise" ? "Custom" : "Custom"}
                    </div>
                    <div className="text-sm text-gray-500">
                      {product.pricing === "Free" ? "Forever" :
                       product.pricing === "Freemium" ? "Tier Available" :
                       product.pricing === "Coming Soon" ? "Launching Soon" :
                       product.pricing === "Enterprise" ? "Pricing" : "Per Month"}
                    </div>
                  </div>
                </div>

                {product.pricing === "Coming Soon" ? (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    disabled
                    className="w-full py-3 bg-gray-300 text-gray-600 rounded-xl font-semibold cursor-not-allowed opacity-60"
                  >
                    {product.cta}
                  </motion.button>
                ) : (
                  <Link to={
                    product.id === "event-ai-agent" ? "/events" :
                    product.id === "preconfigured-agents" ? "/templates" :
                    product.id === "custom-agent" ? "/register" :
                    product.id === "industry-solutions" ? "/contact" :
                    "/register"
                  }>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      className={`w-full py-3 bg-gradient-to-r ${colors.bg} text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all`}
                    >
                      {product.cta}
                    </motion.button>
                  </Link>
                )}
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="glass-effect rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Need a Custom Agent?
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              Our agents can be customized for any business workflow. Plant a
              new agent in your garden or contact us to discuss your specific
              requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  Start Building Free
                </motion.button>
              </Link>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3 glass-effect text-gray-900 rounded-xl font-semibold hover:bg-white/90 transition-all"
              >
                Contact Sales
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsSection;
